import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { MainLayout } from '../components/MainLayout';
import { IncomeExpenseChart } from '../components/charts/IncomeExpenseChart';
import { AssetAllocationChart } from '../components/charts/AssetAllocationChart';
import { SpendingTrendsChart } from '../components/charts/SpendingTrendsChart';
import {
  ReportsService,
  type CashFlowPeriod,
  type SpendingByCategory,
  type IncomeVsExpense,
} from '../services/reports-service';

const DEFAULT_COLORS = [
  '#1e3a5f', '#2563eb', '#16a34a', '#e67e22', '#dc2626',
  '#7c3aed', '#06b6d4', '#f59e0b', '#9C27B0', '#607D8B',
];

export const ReportsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Date range
  const now = new Date();
  const [startDate, setStartDate] = useState(
    `${now.getFullYear()}-01-01`
  );
  const [endDate, setEndDate] = useState(
    now.toISOString().split('T')[0]
  );

  // Data
  const [cashFlow, setCashFlow] = useState<CashFlowPeriod[]>([]);
  const [spending, setSpending] = useState<SpendingByCategory[]>([]);
  const [totalSpending, setTotalSpending] = useState('0');
  const [incomeVsExpense, setIncomeVsExpense] = useState<IncomeVsExpense | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [cashFlowRes, spendingRes, iveRes] = await Promise.all([
        ReportsService.getCashFlow({ start_date: startDate, end_date: endDate }),
        ReportsService.getSpendingByCategory({ start_date: startDate, end_date: endDate }),
        ReportsService.getIncomeVsExpense({ start_date: startDate, end_date: endDate }),
      ]);

      setCashFlow(cashFlowRes.data.periods || []);
      setSpending(spendingRes.data.categories || []);
      setTotalSpending(spendingRes.data.total_amount || '0');
      setIncomeVsExpense(iveRes.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reason: IncomeExpenseChart expects lowercase keys; transform CashFlowPeriod data
  const incomeExpenseData = cashFlow.map((p) => ({
    period: p.period,
    income: parseFloat(p.income),
    expenses: parseFloat(p.expenses),
    net: parseFloat(p.net),
  }));

  // Reason: SpendingTrendsChart shows total expenses per period as a single line
  const spendingTrendsData = cashFlow.map((p) => ({
    period: p.period,
    amount: parseFloat(p.expenses),
  }));

  const spendingChartData = spending.map((s, i) => ({
    name: s.category_name,
    value: parseFloat(s.amount),
    color: s.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
  }));

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyze your income, spending, and cash flow
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            {incomeVsExpense && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Income</Typography>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      ${parseFloat(incomeVsExpense.total_income).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Expenses</Typography>
                    <Typography variant="h5" fontWeight={700} color="error.main">
                      ${parseFloat(incomeVsExpense.total_expenses).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Net Savings</Typography>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      color={parseFloat(incomeVsExpense.net_savings) >= 0 ? 'success.main' : 'error.main'}
                    >
                      ${parseFloat(incomeVsExpense.net_savings).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Savings Rate</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {incomeVsExpense.savings_rate}%
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Tabs */}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Cash Flow" />
              <Tab label="Spending by Category" />
              <Tab label="Spending Trends" />
            </Tabs>

            {/* Cash Flow Tab */}
            {tab === 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Monthly Cash Flow
                  </Typography>
                  <IncomeExpenseChart data={incomeExpenseData} height={400} showNet />
                </CardContent>
              </Card>
            )}

            {/* Spending Tab */}
            {tab === 1 && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* Donut Chart */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      Spending Breakdown
                    </Typography>
                    <AssetAllocationChart data={spendingChartData} height={350} showLegend />
                  </CardContent>
                </Card>

                {/* Category List */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      By Category
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Total: ${parseFloat(totalSpending).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                    {spending.map((cat, i) => (
                      <Box
                        key={cat.category_id || i}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: cat.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
                            }}
                          />
                          <Typography variant="body2" fontWeight={500}>
                            {cat.category_name}
                          </Typography>
                          <Chip
                            label={cat.group_name}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            ${parseFloat(cat.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cat.percentage}%
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    {spending.length === 0 && (
                      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No spending data
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Spending Trends Tab */}
            {tab === 2 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Spending Trends Over Time
                  </Typography>
                  <SpendingTrendsChart data={spendingTrendsData} height={400} />
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Box>
    </MainLayout>
  );
};
